import React, { createContext, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import newCallSound from "../assets/new-call.mp3"; // Certifique-se de que o caminho está correto
import { Device } from "@twilio/voice-sdk";
import { getTwilioVoiceToken } from "../api/client";

const CallContext = createContext();

export const CallProvider = ({ children }) => {
  const [device, setDevice] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [outgoingCall, setOutgoingCall] = useState(null); 
  const [status, setStatus] = useState("Disconnected");
  const [mockMode, setMockMode] = useState(false); // Ative o modo de simulação
  const [audioPermission, setAudioPermission] = useState(false); // Permissão para áudio
  const navigate = useNavigate();

  // Detecta interação inicial do usuário
  useEffect(() => {
    const handleUserInteraction = () => {
      setAudioPermission(true); // Ativa permissão para reprodução de áudio
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
    };

    window.addEventListener("click", handleUserInteraction);
    window.addEventListener("keydown", handleUserInteraction);

    return () => {
      window.removeEventListener("click", handleUserInteraction);
      window.removeEventListener("keydown", handleUserInteraction);
    };
  }, []);

  const setupTwilioDevice = async (channelId) => {
    if (mockMode) return null;
    if (!channelId) return null;
    if (device && device.__xproChannelId === channelId) return device;

    // Tear down previous device (if any)
    try {
      device?.destroy?.();
    } catch (_) {}

    const tokenResp = await getTwilioVoiceToken(channelId);
    const token = tokenResp?.token;
    if (!token) throw new Error("Token de voz inválido.");

    const d = new Device(token, { codecPreferences: ["opus", "pcmu"] });
    d.__xproChannelId = channelId;

    d.on("registered", () => setStatus("Ready"));
    d.on("error", (error) => console.error("Device error:", error));
    d.on("incoming", (call) => {
      setIncomingCall(call);
      playIncomingCallSound();
    });

    await d.register();
    setDevice(d);
    return d;
  };

  // Simulação de chamadas no modo mock
  useEffect(() => {
    if (mockMode) {
      const simulateIncomingCall = setInterval(() => {
        setIncomingCall({
          parameters: { From: "+123456789", Protocol: "ABCD12345" },
          accept: () => {
            setStatus("In Call");
            setIncomingCall(null);
            setOutgoingCall(true);
            navigate("/chat");
          },
          reject: () => {
            setIncomingCall(null);
          },
        });
        playIncomingCallSound();
      }, 15000); // Simula uma chamada a cada 15 segundos

      return () => clearInterval(simulateIncomingCall);
    }
    // playIncomingCallSound está definido abaixo; incluir nas deps causaria ordem inválida
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockMode, navigate]);

  // Reproduz som de chamada
  const playIncomingCallSound = () => {
    if (audioPermission) {
      const audio = new Audio(newCallSound);
      audio.play().catch((error) => console.error("Erro ao reproduzir som:", error));
    }
  };

  // Gerencia o início de uma ligação efetuada
  const startOutgoingCall = async (params) => {
    const toNumber = typeof params === "string" ? params : params?.toNumber;
    const channelId = typeof params === "object" ? params?.channelId : null;
    const leadId = typeof params === "object" ? params?.leadId : null;
    const fromNumber = typeof params === "object" ? params?.fromNumber : null;
    if (mockMode) {
        setOutgoingCall({
            toNumber,
            startTime: new Date(),
            channelId,
            leadId,
            end: () => {
              setOutgoingCall(null);
              setStatus("Disconnected");
            },
          });
          setStatus("Calling");
    } else {
        if (!channelId || !toNumber) {
          console.error("Parâmetros obrigatórios ausentes para originar chamada (channelId/toNumber).");
          return;
        }
      
        try {
          const d = await setupTwilioDevice(channelId);
          if (!d) throw new Error("Device Twilio não configurado.");
          const call = await d.connect({
            params: {
              To: toNumber,
              From: fromNumber || "",
              ChannelId: String(channelId),
              LeadId: leadId ? String(leadId) : "",
            },
          });

          setOutgoingCall({
            toNumber,
            startTime: new Date(),
            channelId,
            leadId,
            callInstance: call,
          });
      
          setStatus("In Call");
          call.on("disconnect", () => {
            setOutgoingCall(null);
            setStatus("Disconnected");
          });
        } catch (error) {
          console.error("Erro ao iniciar a ligação:", error);
          setOutgoingCall(null);
          setStatus("Disconnected");
        }
    }
  };  

  const endOutgoingCall = () => {
    if (mockMode) {
        setOutgoingCall(null);
        setStatus("Calling");
        setStatus("Disconnected");
    } else {
        try {
          outgoingCall?.callInstance?.disconnect?.();
        } catch (_) {}
        setOutgoingCall(null);
        setStatus("Disconnected");
    }
  };  

  const acceptCall = () => {
    if (incomingCall) {
      if (typeof incomingCall.accept === "function") {
        incomingCall.accept();
      }
      setIncomingCall(null);
    }
  };

  const rejectCall = () => {
    if (incomingCall) {
      if (typeof incomingCall.reject === "function") {
        incomingCall.reject();
      }
      setIncomingCall(null);
    }
  };

  /** Define uma chamada recebida via socket (newCall). Só abre a solicitação quando o usuário aceitar. */
  const setIncomingCallFromSocket = ({ conversation, onAccept, onReject }) => {
    setIncomingCall({
      conversation,
      accept: () => {
        onAccept?.();
        setIncomingCall(null);
      },
      reject: () => {
        onReject?.();
        setIncomingCall(null);
      },
    });
  };

  /** Fecha o CallNotification (ex.: quando a chamada é encerrada remotamente em telephony/end). */
  const clearIncomingCall = () => {
    setIncomingCall(null);
  };

  return (
    <CallContext.Provider
      value={{
        device,
        status,
        incomingCall,
        outgoingCall,
        acceptCall,
        rejectCall,
        setIncomingCallFromSocket,
        clearIncomingCall,
        startOutgoingCall,
        endOutgoingCall,
        mockMode,
        setMockMode,
        audioPermission,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useCallContext = () => useContext(CallContext);
